import { TRIAL_DAYS, type Offer } from './trial';

/**
 * The plans, as the builder sells them — the owner's decision of 2026-10-26/27
 * (docs/superpowers/specs/2026-10-27-tiers-and-trial.md). The older one-price plan is no longer sold here.
 *
 * The words live here once, so the reveal, the plan's Build, the free week's "keep it live", the
 * Credits screen, Account and the return from Stripe cannot drift apart on what somebody is being
 * asked to agree to. The server's own table (infra/lambda/shared/plans.ts) decides what Stripe
 * charges; these prices must say the same numbers.
 */
export type PlanId = 'single' | 'monthly' | 'monthly_annual';

export interface Plan {
  id: PlanId;
  name: string;
  /** In whole dollars. */
  amount: number;
  interval: 'month' | 'year';
  /** What it gives, in a line. */
  blurb: string;
}

export const PLANS: Record<PlanId, Plan> = {
  single: { id: 'single', name: 'Single', amount: 10, interval: 'month', blurb: 'One live website · 5 changes a month' },
  monthly: { id: 'monthly', name: 'Monthly', amount: 29, interval: 'month', blurb: 'Up to 10 new apps and 100 changes a month' },
  // The Monthly plan billed yearly. "2 months free" is not claimed (owner, 2026-10-27): just the price.
  monthly_annual: { id: 'monthly_annual', name: 'Monthly', amount: 290, interval: 'year', blurb: 'Up to 10 new apps and 100 changes a month' },
};

export const PLAN_IDS = Object.keys(PLANS) as PlanId[];

export function isPlanId(value: unknown): value is PlanId {
  return typeof value === 'string' && (PLAN_IDS as string[]).includes(value);
}

/** Sold by conversation, never through checkout: "Talk to us" emails the owner (POST /billing/pro). */
export const PRO = {
  name: 'Pro',
  price: 'From $199/month',
  blurb: 'Built with us. We talk first.',
  button: 'Talk to us',
  thanks: 'Thanks — we’ll email you within a day.',
} as const;

/** "$10/month", "$290/year". */
export function priceLine(plan: Plan): string {
  return `$${plan.amount}/${plan.interval}`;
}

/**
 * The founding offer (owner, 2026-10-29): 50% off the first 3 months for the first 50 trials, a Stripe coupon
 * the server applies at checkout to Single and to Monthly billed monthly — never to the yearly price. GET
 * /billing/checkout says whether any are left (api/billing.ts checkoutOffers).
 */
export interface Founding {
  left: number;
  percentOff: number;
  months: number;
}

/** The founding offer, for this plan: null for the yearly price, or when there is none. */
export function foundingFor(plan: Plan, founding: Founding | null | undefined): Founding | null {
  return founding && plan.id !== 'monthly_annual' && founding.left > 0 ? founding : null;
}

const money = (dollars: number): string => `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;

/** What the first months cost with the offer: "$5". */
export function foundingPrice(plan: Plan, f: Founding): string {
  return money(Math.round(plan.amount * (100 - f.percentOff)) / 100);
}

/** "Founding offer: 50% off your first 3 months · 12 left". */
export function foundingBadge(f: Founding): string {
  return `Founding offer: ${f.percentOff}% off your first ${f.months} months · ${f.left} left`;
}

/** What a card says under its price while the trial is on offer: "14 days free, then $10/month". */
export function trialLine(plan: Plan, founding?: Founding | null): string {
  const f = foundingFor(plan, founding);
  if (f) return `${TRIAL_DAYS} days free, then ${foundingPrice(plan, f)}/${plan.interval} for ${f.months} months, then ${priceLine(plan)}`;
  return `${TRIAL_DAYS} days free, then ${priceLine(plan)}`;
}

/** Account's name for it: the yearly Monthly says so. */
export function planLabel(id: PlanId): string {
  return id === 'monthly_annual' ? 'Monthly, billed yearly' : PLANS[id].name;
}

/** A date as people say it: "November 10". Empty for one that cannot be read. */
export function longDate(at: Date | string): string {
  const date = typeof at === 'string' ? new Date(at) : at;
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

/** When a trial taken now first charges the card: the trial's length from today. */
export function trialEndFromNow(now = Date.now()): Date {
  return new Date(now + TRIAL_DAYS * 24 * 60 * 60 * 1000);
}

/*
 * The owner's words for the button and the two lines under it (2026-10-27). The second line is the
 * reminder promise, kept by the day-12 email; the third is the full terms the auto-renewal rules ask
 * for, said before anybody is sent to Stripe.
 */
export const TRIAL_BUTTON = 'Start your free trial';
/** The reminder promise alone, repeated on the return from Stripe and in the "trial started" email. */
export const REMINDER_PROMISE = 'We’ll remind you two days before the trial ends.';
export const TRIAL_REMINDER = `Cancel any time. ${REMINDER_PROMISE}`;
export const SUBSCRIBE_REMINDER = 'Cancel any time.';
/** The no-card trial's line under the button (`trial_card` b, owner 2026-11-01). */
export const NO_CARD_REMINDER = `No card needed. ${REMINDER_PROMISE}`;

const every = (plan: Plan): string => (plan.interval === 'year' ? 'yearly' : 'monthly');

/** "Your card is charged $10 on November 10 unless you cancel. Renews monthly until you cancel." */
export function trialTerms(plan: Plan, chargeAt: Date | string, founding?: Founding | null): string {
  const date = longDate(chargeAt);
  const when = date ? `on ${date}` : `when the ${TRIAL_DAYS}-day trial ends`;
  const f = foundingFor(plan, founding);
  if (f) {
    return `Your card is charged ${foundingPrice(plan, f)} ${when} unless you cancel: ${f.percentOff}% off for ${f.months} months, then ${priceLine(plan)}. Renews ${every(plan)} until you cancel.`;
  }
  return `Your card is charged $${plan.amount} ${when} unless you cancel. Renews ${every(plan)} until you cancel.`;
}

/**
 * The no-card trial's terms: nothing renews by itself, because there is nothing to charge. "Free until
 * November 15. To keep your site live after that, add a card: $10/month, renewing monthly until you
 * cancel. Without one, it pauses and nothing is charged."
 */
export function noCardTerms(plan: Plan, endsAt: Date | string, founding?: Founding | null): string {
  const date = longDate(endsAt);
  const until = date ? `Free until ${date}.` : `Free for ${TRIAL_DAYS} days.`;
  const f = foundingFor(plan, founding);
  const price = f
    ? `${foundingPrice(plan, f)}/${plan.interval} for ${f.months} months (${f.percentOff}% off), then ${priceLine(plan)}`
    : priceLine(plan);
  return `${until} To keep your site live after that, add a card: ${price}, renewing ${every(plan)} until you cancel. Without one, it pauses and nothing is charged.`;
}

/** "Subscribe — $10/month": the plan from today, for an account that has had its trial. */
export function subscribeButton(plan: Plan, founding?: Founding | null): string {
  const f = foundingFor(plan, founding);
  return f ? `Subscribe — ${foundingPrice(plan, f)}/${plan.interval}` : `Subscribe — ${priceLine(plan)}`;
}

/** "Charged today, then every month until you cancel." */
export function subscribeTerms(plan: Plan, founding?: Founding | null): string {
  const f = foundingFor(plan, founding);
  if (f) return `${f.percentOff}% off for ${f.months} months, then ${priceLine(plan)}. Charged today, then every ${plan.interval} until you cancel.`;
  return `Charged today, then every ${plan.interval} until you cancel.`;
}

/**
 * Said on the plan's review step to somebody with no plan, before Build: the choice comes once the
 * app is built (Building.tsx shows it over their site), so this only says what it will cost.
 */
export function planNote(offer: Offer, card: 'now' | 'later' = 'now'): { headline: string; detail: string } {
  return offer === 'trial'
    ? {
        headline: `Free for ${TRIAL_DAYS} days, then from $10/month.`,
        detail: card === 'later' ? 'Choose your plan once it’s built. No card needed to start.' : 'Choose your plan once it’s built. Card required; cancel any time.',
      }
    : { headline: 'Plans from $10/month.', detail: 'Choose your plan once it’s built. Cancel any time.' };
}

/*
 * Whether the plan just chosen went to Stripe with the founding offer, for the page Stripe sends them back
 * to (screens/billing/BillingReturn.tsx), which says what the card will be charged. This tab only.
 */
const FOUNDING_KEY = 'saddle.checkout.founding';
export function rememberFoundingChoice(f: Founding | null): void {
  try {
    if (f) sessionStorage.setItem(FOUNDING_KEY, JSON.stringify(f));
    else sessionStorage.removeItem(FOUNDING_KEY);
  } catch {
    /* said without the discount on the way back */
  }
}
export function rememberedFounding(): Founding | null {
  try {
    const f = JSON.parse(sessionStorage.getItem(FOUNDING_KEY) ?? 'null') as Founding | null;
    return f && typeof f.percentOff === 'number' && typeof f.months === 'number' ? f : null;
  } catch {
    return null;
  }
}

/**
 * A price made for one account (2026-11-01): "$2.99/month for 3 months, then $10/month". Charged from
 * today, no trial: it keeps a site live that is already built.
 */
export interface PersonalOffer { plan: PlanId; amount: number; months: number }
const dollars = (cents: number): string => `$${cents % 100 === 0 ? cents / 100 : (cents / 100).toFixed(2)}`;
export function personalOfferLines(o: PersonalOffer): { title: string; button: string; terms: string } {
  const plan = PLANS[o.plan];
  const per = plan.interval;
  return {
    title: `Keep your site live for ${dollars(o.amount)}/${per}`,
    button: `Keep it live — ${dollars(o.amount)}/${per}`,
    terms: `${dollars(o.amount)} today and each ${per} for ${o.months} ${per}s, then ${priceLine(plan)} (${plan.name}) until you cancel. Cancel any time.`,
  };
}

/** The offer's terms, for the page Stripe sends them back to (BillingReturn), this tab only. */
const OFFER_TERMS_KEY = 'saddle.checkout.offerTerms';
export function rememberOfferTerms(terms: string | null): void {
  try {
    if (terms) sessionStorage.setItem(OFFER_TERMS_KEY, terms);
    else sessionStorage.removeItem(OFFER_TERMS_KEY);
  } catch {
    /* the plain plan's words on the way back */
  }
}
export function rememberedOfferTerms(): string | null {
  try {
    return sessionStorage.getItem(OFFER_TERMS_KEY);
  } catch {
    return null;
  }
}
